import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import type { InvitePreviewDTO, WorkspaceDTO } from '@regent/shared';
import { Button, Skeleton } from '@/components/ui';
import { AuthLayout } from '@/features/auth/auth-layout';
import { SignInForm, SignUpForm } from '@/features/auth/forms';
import { api, errorMessage } from '@/lib/api';
import { authClient } from '@/lib/auth';
import { keys, meQuery } from '@/lib/queries';

export const Route = createFileRoute('/invite/$token')({ component: InvitePage });

function InvitePage() {
  const { token } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const me = useQuery(meQuery);
  const invite = useQuery({
    queryKey: ['invite', token],
    queryFn: async () => (await api.get<{ invite: InvitePreviewDTO }>(`/invites/${token}`)).invite,
    retry: false,
  });
  const [mode, setMode] = useState<'signup' | 'signin'>('signup');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const accept = async () => {
    setError(null);
    setPending(true);
    try {
      const { workspace } = await api.post<{ workspace: WorkspaceDTO }>(`/invites/${token}/accept`);
      await qc.invalidateQueries({ queryKey: keys.me });
      await navigate({ to: '/w/$workspaceId/home', params: { workspaceId: workspace.id } });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  };

  if (invite.isLoading || me.isLoading) {
    return (
      <AuthLayout title="Loading invite">
        <div className="space-y-3" role="status" aria-label="Loading">
          <Skeleton className="h-5 w-3/4" />
          <Skeleton className="h-9" />
        </div>
      </AuthLayout>
    );
  }

  const data = invite.data;
  if (!data || data.expired) {
    return (
      <AuthLayout title={data?.expired ? 'This invite has expired' : 'Invite not found'}>
        <p className="text-center text-sm text-fg-muted" role="alert">
          Ask the person who invited you to send a new invite.
        </p>
        <Button asChild className="mt-4 w-full">
          <Link to="/">Go to Regent</Link>
        </Button>
      </AuthLayout>
    );
  }

  const user = me.data?.user;
  const emailMatches = user?.email.toLowerCase() === data.email.toLowerCase();

  return (
    <AuthLayout
      title={`Join ${data.workspaceName}`}
      subtitle={`${data.inviterName} invited ${data.email} to join as ${data.role === 'admin' ? 'an admin' : `a ${data.role}`}.`}
    >
      {user ? (
        <div className="space-y-3">
          {emailMatches || data.accepted ? (
            <>
              <p className="text-sm text-fg-muted">
                You are signed in as <span className="font-medium text-fg">{user.email}</span>.
              </p>
              <Button
                variant="primary"
                size="lg"
                className="w-full"
                loading={pending}
                onClick={() => void accept()}
              >
                {data.accepted ? 'Open workspace' : 'Accept invite'}
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-fg-muted" role="alert">
                This invite was sent to <span className="font-medium text-fg">{data.email}</span>, but you are
                signed in as {user.email}.
              </p>
              <Button
                size="lg"
                className="w-full"
                onClick={async () => {
                  await authClient.signOut();
                  await qc.resetQueries({ queryKey: keys.me });
                }}
              >
                Sign out and switch account
              </Button>
            </>
          )}
          {error ? (
            <p role="alert" className="text-sm text-danger">
              {error}
            </p>
          ) : null}
        </div>
      ) : (
        <>
          {mode === 'signup' ? (
            <SignUpForm email={data.email} redirectTo={`/invite/${token}`} onDone={() => void accept()} />
          ) : (
            <SignInForm email={data.email} redirectTo={`/invite/${token}`} onDone={() => void accept()} />
          )}
          <p className="mt-4 text-center text-sm text-fg-muted">
            {mode === 'signup' ? 'Already have an account?' : 'Need an account?'}{' '}
            <button
              className="font-medium text-accent-text hover:underline"
              onClick={() => setMode(mode === 'signup' ? 'signin' : 'signup')}
            >
              {mode === 'signup' ? 'Sign in instead' : 'Create one'}
            </button>
          </p>
          {error ? (
            <p role="alert" className="mt-2 text-center text-sm text-danger">
              {error}
            </p>
          ) : null}
        </>
      )}
    </AuthLayout>
  );
}
